import React, { useEffect, useState, useRef, useCallback } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import { PayloadAction } from '@reduxjs/toolkit';
import { useNavigate, useLocation } from 'react-router-dom';
import { Spin, Row, Col } from 'antd';
import queryString from 'query-string';
import pluralize from 'pluralize';
import { useDidUpdate } from 'rooks';

import {
  AssignmentStatusTypeEnum,
  AssignmentTitleInfoModel,
  AssignmentStageFilterEnum,
} from 'api/backofficeApiClient';
import { getAssignments } from 'redux/reducers/assignmentSlice';
import { AdminPagedAssignmentResponseModel } from 'api/adminApiClient';
import { isCorporateTASelector } from 'redux/selectors/authenticationSelectors';
import { assignmentCreatedAtSelector } from 'redux/selectors/assignmentSelectors';
import { typeConversionToNumberArray, typeConversionToStringArray } from 'utils/convertUtils';
import AssignmentCard from '../AssignmentCard';
import { AssignmentFilterTypes, AssignmentFilterOptions } from '../enums';
import AssignmentsFilter, { AssignmentFilters } from '../AssignmentsFilter';
import { AssigmentQueryType } from '../AssignmentsSection/AssignmentsSection';
import BaseSearch from 'components/wrappers/BaseSearch';
import { useAppSelector } from 'hooks/useAppSelector';
import { useAppDispatch } from 'hooks/useAppDispatch';

import styles from '../../client/ClientRequestsSection/ClientRequestsSection.module.less';

type Props = {
  isAdmin?: boolean;
};

export default function AssignmentRequestsSection(props: Props) {
  const { isAdmin } = props;
  const latestRequestId = useRef(0);
  const infiniteScrollParentRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { query, customer, section, worker, company, workerType } =
    queryString.parse(location.search);
  const [assignments, setAssignments] = useState<AssignmentTitleInfoModel[]>(
    [],
  );
  const [totalCount, setTotalCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);
  const isCorporateTA = useAppSelector(isCorporateTASelector);
  const getInitialFilters = (): AssignmentFilters => {
    const filters: AssignmentFilters = {
      [AssignmentFilterTypes.Worker]: typeConversionToStringArray(worker),
      [AssignmentFilterTypes.WorkerType]:
        typeConversionToNumberArray(workerType),
    };

    if (isAdmin) {
      filters[AssignmentFilterTypes.Company] =
        typeConversionToStringArray(company);
    }

    if (!isCorporateTA) {
      filters[AssignmentFilterTypes.Customer] =
        typeConversionToStringArray(customer);
    }

    return filters;
  };

  const [filters, setFilters] =
    useState<AssignmentFilters>(getInitialFilters());
  const assignmentCreatedAt = useAppSelector(assignmentCreatedAtSelector);
  const dispatch = useAppDispatch();

  useDidUpdate(() => {
    const queryData: AssigmentQueryType = {
      query,
      section,
    };
    if (!isCorporateTA) {
      queryData[AssignmentFilterTypes.Customer] =
        filters[AssignmentFilterTypes.Customer];
    }
    if (isAdmin) {
      queryData[AssignmentFilterTypes.Company] =
        filters[AssignmentFilterTypes.Company];
    }
    queryData[AssignmentFilterTypes.Worker] =
      filters[AssignmentFilterTypes.Worker];
    queryData[AssignmentFilterTypes.WorkerType] =
      filters[AssignmentFilterTypes.WorkerType];

    navigate(`${location.pathname}?${queryString.stringify(queryData)}`, {
      replace: true,
    });
  }, [filters, query]);

  useEffect(() => {
    initInfinityScroll();
  }, [assignmentCreatedAt, query, filters]);

  const initInfinityScroll = () => {
    setIsVisible(false);
    setAssignments([]);
    setIsInitialDataLoaded(false);
    setHasMore(true);
    setTotalCount(0);
    const timer = setTimeout(() => {
      // to destroy InfiniteScroll
      setIsVisible(true);
    }, 0);

    return () => clearTimeout(timer);
  };

  const getTotalCountLabel = () => {
    const requestsWord = pluralize('request', totalCount);

    return `${totalCount} assignment ${requestsWord}`;
  };

  const getNotFoundMessage = () =>
    'Great job! None of your assignments are pending.';

  const onSetAssignments = useCallback(
    (result: PayloadAction<AdminPagedAssignmentResponseModel>) => {
      const loadedAssignments = result.payload.details || [];
      const totalCount = result.payload.totalCount || 0;
      const newAssignments = [...assignments, ...loadedAssignments];
      const hasMoreRows = newAssignments.length < totalCount;

      setHasMore(hasMoreRows);
      setAssignments(newAssignments);
      setTotalCount(totalCount);
    },
    [assignments],
  );

  const onLoadMore = useCallback(
    async (pageNumber: number) => {
      const requestId = Date.now();
      latestRequestId.current = requestId;

      const result = await dispatch(
        getAssignments({
          pageNumber,
          requestId,
          isAdminRole: isAdmin,
          searchQuery: query ? query.trim() : undefined,
          stage: AssignmentStageFilterEnum.All,
          statuses: [AssignmentStatusTypeEnum.Request],
          customerIds: !filters[AssignmentFilterTypes.Customer]?.includes(
            AssignmentFilterOptions.All,
          )
            ? filters[AssignmentFilterTypes.Customer]
            : [],
          workerIds: !filters[AssignmentFilterTypes.Worker]?.includes(
            AssignmentFilterOptions.All,
          )
            ? filters[AssignmentFilterTypes.Worker]
            : [],
          workerTypes: filters[AssignmentFilterTypes.WorkerType],
          companyIds: !filters[AssignmentFilterTypes.Company]?.includes(
            AssignmentFilterOptions.All,
          )
            ? filters[AssignmentFilterTypes.Company]
            : [],
        }),
      );

      if (result.meta.arg.requestId === latestRequestId.current) {
        if (getAssignments.fulfilled.match(result)) {
          onSetAssignments(result);
        } else {
          setHasMore(false);
        }

        setIsInitialDataLoaded(true);
      }
    },
    [dispatch, onSetAssignments, isAdmin, query, filters],
  );

  return (
    <div ref={infiniteScrollParentRef} className="page-wrapper scroll-box">
      <div className="page-box page-holder page-inner">
        <div className="page-inner-container">
          <Row gutter={[20, 10]} className={styles.row}>
            <Col xs={24} md={6}>
              <BaseSearch
                searchQuery={query}
                placeholder="Search by assignment name, ID"
              />
            </Col>

            <AssignmentsFilter
              assignmentStatuses={[AssignmentStatusTypeEnum.Request]}
              filters={filters}
              onChange={setFilters}
              isPendingSection={true}
            />
          </Row>

          {isInitialDataLoaded && (
            <Row gutter={[20, 10]}>
              <Col xs={24}>
                <>
                  {!!totalCount && (
                    <p className="page-total-count">{getTotalCountLabel()}</p>
                  )}

                  {!totalCount && (
                    <span className="no-data-msg">{getNotFoundMessage()}</span>
                  )}
                </>
              </Col>
            </Row>
          )}

          {isVisible && (
            <InfiniteScroll
              pageStart={-1}
              loadMore={onLoadMore}
              hasMore={hasMore}
              loader={<Spin key={0} />}
              useWindow={false}
              getScrollParent={() => infiniteScrollParentRef.current}
            >
              <Row gutter={[20, 20]}>
                {assignments.map((assignment) => (
                  <Col
                    key={assignment.assignmentId}
                    xs={24}
                    md={12}
                    className="d-flex"
                  >
                    <AssignmentCard
                      assignment={assignment}
                      className="stretch-card"
                      shouldDisplayStatus={false}
                    />
                  </Col>
                ))}
              </Row>
            </InfiniteScroll>
          )}
        </div>
      </div>
    </div>
  );
}
